import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponseWith } from './pagination.js';
import { AuthorshipSchema, CurrencyNullishSchema, IsoDateSchema, MessageSchema, MoneyStringSchema, } from './primitives.js';
const c = initContract();
/**
 * The chart of accounts — FIN-01 in full.
 *
 * Reads: the flat list (every picker in the product derives from it), the
 * tree-with-balances (FIN-01 §4/§5b: the roll-up follows the header flag,
 * POSTED entries only per R9/BR-FIN-01-07a, period-filtered per
 * BR-FIN-01-14), and one account's ledger drill (§5d).
 *
 * Writes: create/edit/deactivate/delete an account, and BR-FIN-01-19's
 * "Load starter chart" (empty chart only — signup already seeds; the button
 * exists for the spec-literal empty tenant).
 *
 * Permission area: `accounting` (16.2.2 row 6), one action per route as 16.3
 * assigns them — view reads, create makes, edit changes (deactivation is an
 * edit: the account survives), delete removes. FIN-01 §9's "maintenance
 * permission" resolves to the accounting area's Create/Edit/Delete switches;
 * PART 16 is the authority on who holds them (Administrator RCUDA,
 * Accountant RCUA, AP Clerk —).
 */
// ── The fixed sentences of FIN-01 §10/§14 ───────────────────────────────────
export const ACCOUNT_MESSAGES = {
    /** BR-FIN-01-02 / ERR-FIN-01-01 — e.g. "Code 5000 is already used by Expenses." */
    codeUsed: (code, holder) => `Code ${code} is already used by ${holder}.`,
    /** BR-FIN-01-06 / ERR-FIN-01-02, quoted exactly. */
    becomeHeadingWithPostings: 'This account has postings and cannot become a heading.',
    /** FIN-01 §7 — Header → Postable requires no children. */
    headingWithChildren: 'This heading has accounts inside it and cannot become postable.',
    /**
     * BR-FIN-01-18 — e.g. "1200 Accounts receivable holds 142,300.00. Move the
     * balance to another account first." Balance preformatted by the caller.
     */
    deactivateWithBalance: (code, name, balance) => `${code} ${name} holds ${balance}. Move the balance to another account first.`,
    /** BR-FIN-01-10 / ERR-FIN-01-04 — e.g. "This account has 412 postings. Deactivate it instead." */
    deleteWithPostings: (count) => `This account has ${count} postings. Deactivate it instead.`,
    /**
     * MD-06-D3 — the type is frozen once any journal line uses the account,
     * because the type decides which report the account appears on and moving it
     * silently restates history. PART 5 states this and FIN-01 does not, so it is
     * not overridden by the "FIN-01 wins" clause — FIN-01 is simply silent.
     *
     * Worded on MD-03's locked-field pattern ("Its tracking cannot change —
     * create a new item"), with FIN-01's own "<code> <name>" subject that
     * `deactivateWithBalance` already uses.
     */
    typeLocked: (code, name, lines) => `${code} ${name} is on ${lines} journal ${lines === 1 ? 'line' : 'lines'}. ` +
        `Its type cannot change — create a new account.`,
    /** Structure is dismantled leaf-first; a heading with children stays. */
    deleteWithChildren: 'This heading has accounts inside it and cannot be deleted.',
    /**
     * ERR-FIN-01-03, verbatim — "Cash & Cash Equivalents is a heading. Post to
     * one of the accounts inside it." The row names the ACCOUNT and not its
     * code, which is why this takes the name alone.
     *
     * The refusal is raised by the ledger engine at the posting chokepoint
     * (BR-FIN-01-05 / BR-FIN-02-04) and reaches the reader through
     * `LedgerRefusalFilter`, from every posting door there is.
     */
    headingOnLine: (name) => `${name} is a heading. Post to one of the accounts inside it.`,
    /**
     * BR-FIN-01-09 — "an inactive account must not be postable anywhere" — with
     * FIN-01's own `<code> <name>` subject, the one `deactivateWithBalance`
     * above uses. The rule has no ERR row of its own; this is the sentence the
     * engine has always composed, minus the rule id §4.16 keeps out of a
     * message a person reads.
     */
    inactiveOnLine: (code, name) => `Account ${code} ${name} is inactive and cannot be posted to.`,
    /** BR-FIN-01-04 — the server half; the picker never offers these (ERR-FIN-01-05). */
    parentLoop: 'An account cannot sit inside itself or one of its own sub-accounts.',
    parentNotFound: 'The chosen parent account does not exist.',
    /** BR-FIN-01-19 — the button exists only while the chart is empty. */
    chartNotEmpty: 'The chart of accounts already has accounts. Load starter chart is offered only while the chart is empty.',
    notFound: 'This account does not exist.',
    /**
     * BR-FIN-01-17 — "a rule that cannot find its account must stop the posting
     * and say which company default is missing." Composed by
     * `requireAccountsByCode` (apps/api platform/chart-accounts.ts), which is
     * the funnel every automatic posting resolves its accounts through; it
     * lived there as a raw template literal until D46 moved the sentence into
     * the contract so the refusal could carry its key and params.
     */
    missingFromChart: (code) => `Account ${code} is missing from the chart of accounts. Load the starter chart before posting.`,
};
/**
 * D46 — FIN-01's CONVERTED parameterised refusals, structured. The first two
 * are the ledger engine's non-postable pair, surfaced by
 * `LedgerRefusalFilter` from every posting door; the third is
 * BR-FIN-01-17's missing-default stop. Sentence parity with the
 * `ACCOUNT_MESSAGES` twins and the roster of still-unconverted siblings are
 * pinned by `refusal-census.spec.ts`.
 */
export const ACCOUNT_REFUSALS = {
    headingOnLine: (name) => ({
        message: ACCOUNT_MESSAGES.headingOnLine(name),
        key: 'coa.errHeadingOnLine',
        params: { name },
    }),
    inactiveOnLine: (code, name) => ({
        message: ACCOUNT_MESSAGES.inactiveOnLine(code, name),
        key: 'coa.errInactiveOnLine',
        params: { code, name },
    }),
    missingFromChart: (code) => ({
        message: ACCOUNT_MESSAGES.missingFromChart(code),
        key: 'coa.errMissingFromChart',
        params: { code },
    }),
    // ── d46tail: FIN-01's four remaining parameterised refusals ───────────────
    /** BR-FIN-01-02 — the code is taken; `holder` is the account name that has
     *  it (a display value). */
    codeUsed: (code, holder) => ({
        message: ACCOUNT_MESSAGES.codeUsed(code, holder),
        key: 'coa.errCodeUsed',
        params: { code, holder },
    }),
    /** BR-FIN-01-18 — `balance` is PRE-FORMATTED by the raise site's grouping
     *  formatter; it rides as a display string, never a raw number. */
    deactivateWithBalance: (code, name, balance) => ({
        message: ACCOUNT_MESSAGES.deactivateWithBalance(code, name, balance),
        key: 'coa.errDeactivateWithBalance',
        params: { code, name, balance },
    }),
    /** BR-FIN-01-10 — `count` is a genuine posting count (raw); the twin says
     *  "postings" flat, so the en value mirrors it flat. */
    deleteWithPostings: (count) => ({
        message: ACCOUNT_MESSAGES.deleteWithPostings(count),
        key: 'coa.errDeleteWithPostings',
        params: { count },
    }),
    /** MD-06-D3 — `lines` is a genuine journal-line count (raw); the twin forks
     *  line/lines (noun-only plural). */
    typeLocked: (code, name, lines) => ({
        message: ACCOUNT_MESSAGES.typeLocked(code, name, lines),
        key: 'coa.errTypeLocked',
        params: { code, name, lines },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const AccountTypeSchema = z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']);
/**
 * MD-RULE-5's six fields, with ONE honest divergence: `updatedAt` is rule-coverage:ignore
 * nullable. `accounts.updated_at` arrived late (20260902100000) and without
 * a default — a `DEFAULT now()` would have stamped migration time as a
 * change time on every account that already existed — so NULL is a real
 * wire value meaning "no change recorded", and the screen must draw nothing
 * for the changed half rather than a date the table never knew. Every other
 * master kind spreads `AuthorshipSchema` verbatim; this shape exists so the
 * difference is declared once, here, instead of loosening the shared schema
 * for nine kinds that never emit null.
 */
export const AccountAuthorshipSchema = z.object({
    ...AuthorshipSchema.shape,
    updatedAt: z.string().nullable(),
});
export const AccountSchema = z
    .object({
    // MD-RULE-5's six fields (updatedAt nullable — see the shape above). rule-coverage:ignore
    ...AccountAuthorshipSchema.shape,
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    /** BR-FIN-01-11 — every account carries both names; the tree shows the reader's. */
    nameAr: z.string(),
    type: AccountTypeSchema,
    /** Headers (postable = false) are structure, never posting targets. */
    postable: z.boolean(),
    active: z.boolean(),
    /** The parent account, null at top level. */
    parentId: z.uuid().nullable(),
    /** Null means the base currency (FIN-01 field 6). */
    currency: z.string().nullable(),
    description: z.string().nullable(),
})
    .strict();
/** A signed NUMERIC(15,2) string — balances may be negative (contra accounts). */
const SignedMoneyStringSchema = z.string().regex(/^-?\d{1,13}\.\d{2}$/);
/**
 * One tree row: the account plus its period figures. Debits/credits/balance
 * roll up through headers at any depth (BR-FIN-01-07); balance is SIGNED on
 * the account's normal side — the unsigned tree display is the UI's job
 * (BR-FIN-01-08).
 */
export const AccountTreeRowSchema = z
    .object({
    ...AccountSchema.shape,
    debit: MoneyStringSchema,
    credit: MoneyStringSchema,
    /** Signed: a contra balance comes out negative (shown red on the card). */
    balance: SignedMoneyStringSchema,
})
    .strict();
/** One drill row (§5d): a posted journal line touching the account. */
export const AccountLedgerRowSchema = z
    .object({
    entryId: z.uuid(),
    entryNo: z.number().int(),
    /**
     * JE-#### as this company prints it. The drill-down used to build it in
     * the browser from `entryNo`, which a tenant who renames their entries on
     * MD-13 would never have seen change.
     */
    code: z.string(),
    entryDate: IsoDateSchema,
    /** The entry's reference/memo — how the posting describes itself. */
    reference: z.string().nullable(),
    lineMemo: z.string().nullable(),
    debit: MoneyStringSchema,
    credit: MoneyStringSchema,
    /** Running balance on the account's normal side, signed. */
    balance: SignedMoneyStringSchema,
})
    .strict();
/**
 * BR-FIN-01-01 — "An account must have a code, a name and a type." This
 * schema is the enforcing line: `code` and `name` are trim-then-min(1) so
 * whitespace is not a value, and `type` is required with no default. The
 * update schema below relaxes them only to OPTIONAL (absent = unchanged),
 * never to empty — so no route can create or edit an account out of its
 * three identities. Proven by the accounting e2e's three one-absence-at-a-
 * time refusals.
 */
export const CreateAccountSchema = z
    .object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().min(1).max(200),
    /** Defaults to the English name when the tenant has not typed one yet. */
    nameAr: z.string().trim().min(1).max(200).optional(),
    type: AccountTypeSchema,
    parentId: z.uuid().nullish(),
    /** Header/group = NOT postable (FIN-01 field 7). Defaults to postable. */
    postable: z.boolean().optional(),
    currency: CurrencyNullishSchema,
    description: z.string().max(2000).nullish(),
})
    .strict();
export const UpdateAccountSchema = z
    .object({
    code: z.string().trim().min(1).max(20).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    nameAr: z.string().trim().min(1).max(200).optional(),
    type: AccountTypeSchema.optional(),
    parentId: z.uuid().nullish(),
    postable: z.boolean().optional(),
    /** Deactivation rides the edit (BR-FIN-01-18 guards it). */
    active: z.boolean().optional(),
    currency: CurrencyNullishSchema,
    description: z.string().max(2000).nullish(),
})
    .strict();
const PeriodQuerySchema = z
    .object({
    /** Inclusive entry-date bounds (BR-FIN-01-14). */
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
})
    .strict();
// ── Routes ──────────────────────────────────────────────────────────────────
export const accountsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/accounts',
        responses: {
            200: z.object({ accounts: AccountSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The chart of accounts in code order (accounting.view).',
    },
    tree: {
        method: 'GET',
        path: '/api/accounts/tree',
        query: PeriodQuerySchema,
        responses: {
            200: z
                .object({
                accounts: AccountTreeRowSchema.array(),
                /** Grand totals over postable accounts — the trial-balance checksum. */
                totalDebit: MoneyStringSchema,
                totalCredit: MoneyStringSchema,
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The tree with rolled-up POSTED balances, period-filtered (accounting.view).',
    },
    ledger: {
        method: 'GET',
        path: '/api/accounts/:accountId/ledger',
        pathParams: z.object({ accountId: z.uuid() }).strict(),
        query: pagedQuery(PeriodQuerySchema.shape),
        responses: {
            /*
             * The three footer figures sit BESIDE the rows, not inside them, and
             * they are figures about the whole period rather than about the page —
             * the same contract `pagedResponseWith` gives INV-05's summary cards.
             *
             * The `balance` column is the one place in this product where paging
             * could change a NUMBER rather than hide a row: it is cumulative from
             * the first line of the range, so it is computed as a window over the
             * whole filtered set BEFORE the LIMIT. See `AccountsService.ledger`.
             */
            200: pagedResponseWith('rows', AccountLedgerRowSchema, {
                /** The §5d closing row — of the PERIOD, on every page. */
                closingBalance: SignedMoneyStringSchema,
                totalDebit: MoneyStringSchema,
                totalCredit: MoneyStringSchema,
            }),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "One account's posted lines with a running balance (accounting.view), PAGED (NFR-42) — the running balance is a SQL window over the whole period, so page 2 continues it rather than restarting it.",
    },
    create: {
        method: 'POST',
        path: '/api/accounts',
        body: CreateAccountSchema,
        responses: {
            201: AccountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Create an account (accounting.create). BR-FIN-01-20: periods never block this.',
    },
    update: {
        method: 'PATCH',
        path: '/api/accounts/:accountId',
        pathParams: z.object({ accountId: z.uuid() }).strict(),
        body: UpdateAccountSchema,
        responses: {
            200: AccountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit an account (accounting.edit): identity, parent, header flip (BR-FIN-01-06), active (BR-FIN-01-18).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/accounts/:accountId',
        body: c.noBody(),
        pathParams: z.object({ accountId: z.uuid() }).strict(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete an account with no postings and no children (accounting.delete).',
    },
    loadStarterChart: {
        method: 'POST',
        path: '/api/accounts/starter-chart',
        body: c.noBody(),
        responses: {
            201: z.object({ accounts: AccountSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'BR-FIN-01-19 — seed the FIN-01 §5c chart into an EMPTY chart (accounting.create).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=accounts.js.map